import { AddSquareLightBig as AddIcon, BellFill, BellLight, FullArrowLight as FullIcon } from '@repo/go-icons';
import { Button, Tooltip, Hint, HINT_DEFAULT_STYLE_OPTIONS } from '@repo/go-ui';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';

import { ALERTS_MANAGER_AVAILABLE, EXPORT_CSV_AVAILABLE } from 'src/api/consts';
import type { TabEntity } from 'src/entities/tab/TabEntity';
import { useService } from 'src/packages/di';
import { TabsMenu } from 'src/pages/dashboard/features/tabs/components/tabs-menu/TabsMenu';

import { UserExportTasksButton } from '../user-export-tasks/UserExportTasksButton';
import type { WorkspaceHintsStore } from '../workspace/services/WorkspaceHintsStore';
import { HintNames } from '../workspace/services/WorkspaceHintsStore';
import type { WorkspaceStore } from '../workspace/Workspace.store';

import styles from './Toolbar.module.scss';

type Props = {
  workspaceStore: WorkspaceStore;
  workspaceHintsStore: WorkspaceHintsStore;
  tabsEntities: TabEntity[];
  actualTab: TabEntity | null;
  onSetActiveKey(key?: string): void;
  onCreateTab(): void;
  onRemoveTab(id: string): void;
  onTabClose?(id: string): void;
  onOpenInNewWindow?(tabId: string): void;
  onFullscreen: VoidFunction;
  onChangeTabName(id: string, name: string): void;
  onSaveTabAsTemplateModalOpen(): void;
  onTabSavedAsTemplateChange(tab: TabEntity | null): void;
  onUpdateTemplate(tab: TabEntity): void;
  disabledTabs: string[];
  activeKey?: string;
  onTabFocus?(id: string): void;
  onCreateWellListWidget?(): void;
  onCreateWellsFilterWidget?(): void;
  onCreateWellLogsManagerWidget?(): void;
  onCreateContentWidget: VoidFunction;
};

export const RightToolbar = observer(function RightToolbar({
  workspaceHintsStore,
  activeKey,
  tabsEntities,
  disabledTabs,
  onSaveTabAsTemplateModalOpen,
  onTabSavedAsTemplateChange,
  onUpdateTemplate,
  onFullscreen,
  onTabFocus,
  onSetActiveKey,
  onCreateTab,
  onRemoveTab,
  onTabClose,
  onChangeTabName,
  onOpenInNewWindow,
}: Props) {
  const { t } = useTranslation();
  const anomalyNotificationsService = useService('anomalyNotificationsService');

  const addNewTabHint = workspaceHintsStore.hintsMap.get(HintNames.addNewTab);
  const notificationsTooltip = anomalyNotificationsService.isNotificationsEnabled
    ? t('dashboard:Toolbar.disableNotifications')
    : t('dashboard:Toolbar.enableNotifications');

  return (
    <div className={styles.toolbar}>
      <div className={styles.tabsButtons}>
        <Tooltip.Multi
          tooltips={[
            {
              key: 'addNewTabTooltip',
              title: t('dashboard:Toolbar.addNewTab'),
              delay: 400,
              trigger: addNewTabHint?.isVisible ? [] : undefined,
            },
            {
              key: 'addNewTabHint',
              title: <Hint.HintContent description={t(addNewTabHint?.data.description ?? '')} />,
              open: !!addNewTabHint?.isVisible,
              ...HINT_DEFAULT_STYLE_OPTIONS,
            },
          ]}
        >
          <Button
            variant="flat"
            icon={<AddIcon className={styles.icon} />}
            onClick={onCreateTab}
            className={styles.addTabButton}
          />
        </Tooltip.Multi>
        <TabsMenu
          disabledTabs={disabledTabs}
          onTabFocus={onTabFocus}
          onOpenInNewWindow={onOpenInNewWindow}
          activeKey={activeKey}
          tabs={tabsEntities}
          onTabRemove={onRemoveTab}
          onTabClose={onTabClose}
          onActiveKeyChange={onSetActiveKey}
          onChangeTabName={onChangeTabName}
          onSaveTabAsTemplateModalOpen={onSaveTabAsTemplateModalOpen}
          onUpdateTemplate={onUpdateTemplate}
          onTabSavedAsTemplateChange={onTabSavedAsTemplateChange}
        />
      </div>
      <div className={styles.widgetsToolbar}>
        {EXPORT_CSV_AVAILABLE && <UserExportTasksButton />}
        {ALERTS_MANAGER_AVAILABLE && (
          <Tooltip title={notificationsTooltip} delay={400}>
            <Button
              variant="flat"
              icon={
                anomalyNotificationsService.isNotificationsEnabled ? (
                  <BellFill className={styles.icon} />
                ) : (
                  <BellLight className={styles.icon} />
                )
              }
              onClick={anomalyNotificationsService.toggleNotifications}
              className={styles.toolbarIcon}
            />
          </Tooltip>
        )}
        <Tooltip title={t('dashboard:Toolbar.enableFullScreen')} delay={400}>
          <Button
            variant="flat"
            icon={<FullIcon className={styles.icon} />}
            title={t('dashboard:Toolbar.enableFullScreen')}
            onClick={onFullscreen}
            className={styles.toolbarIcon}
          />
        </Tooltip>
      </div>
    </div>
  );
});
